import { MapPinIcon, PhotoIcon } from "@heroicons/react/20/solid";
import { storyblokEditable } from "@storyblok/react/rsc";
import { track } from "@vercel/analytics";
import { clsx } from "clsx";
import dynamic from "next/dynamic";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useReducer, useState } from "react";

import type {
  CampsiteOverviewStoryblok,
  CampsiteStoryblok,
} from "@/component-types-sb";
import { Card } from "@/components/layout/Card";
import { BodyText, Divider, H2 } from "@/components/typography";

const Map = dynamic(() =>
  import("@/components/layout/Map").then((module) => module.Map),
);

type CampsiteOverviewProps = {
  blok: CampsiteOverviewStoryblok;
};

const reducer = (state: any, action: { type: "card" | "map" | "image" }) => {
  return { ...state, ...action };
};

const locations = [
  {
    title: "Area 39",
    href: "/area39",
    x: 722.05,
    y: 569.98,
    width: 107.21,
    height: 29.54,
  },
  {
    title: "Bad Dürkheim",
    href: "/bad-duerkheim",
    x: 348.99,
    y: 1134.5,
    width: 147.69,
    height: 53.61,
  },
  {
    title: "Bad Kissingen",
    href: "/bad-kissingen",
    x: 611.56,
    y: 893.81,
    width: 137.85,
    height: 59.08,
  },
  {
    title: "Bernkastel-Kues",
    href: "/bernkastel-kues",
    x: 194.74,
    y: 988.99,
    width: 150.97,
    height: 56.89,
  },
  {
    title: "Bleckede",
    href: "/elbtalaue-bleckede",
    x: 719.86,
    y: 342.43,
    width: 140.03,
    height: 51.42,
  },
  {
    title: "Burgen / Mosel",
    href: "/mosel-burgen",
    x: 106.12,
    y: 806.29,
    width: 148.79,
    height: 53.61,
  },
  {
    title: "Dorum",
    href: "/dorum-nordsee",
    x: 259.28,
    y: 224.27,
    width: 105.03,
    height: 32.82,
  },
  {
    title: "Eckwarderhörne",
    href: "/eckwarderhoerne-nordsee",
    x: 117.06,
    y: 332.58,
    width: 198.02,
    height: 32.82,
  },
  {
    title: "Eschwege",
    href: "/eschwege",
    x: 476.99,
    y: 687.04,
    width: 145.5,
    height: 35,
  },
  {
    title: "Essen-Werden",
    href: "/essen-werden",
    x: 121.44,
    y: 615.93,
    width: 171.76,
    height: 33.91,
  },
  {
    title: "Frickenhausen",
    href: "/frickenhausen",
    x: 438,
    y: 1049,
    width: 189.26,
    height: 35,
  },
  {
    title: "Hamburg",
    href: "/hamburg",
    x: 620.31,
    y: 272.41,
    width: 119.25,
    height: 30.63,
  },
  {
    title: "Haren",
    href: "/haren",
    x: 89.71,
    y: 423.38,
    width: 126,
    height: 31.73,
  },
  {
    title: "Hennesee",
    href: "/hennesee-meschede",
    x: 300.85,
    y: 666.26,
    width: 148.79,
    height: 36.1,
  },
  {
    title: "Hünfeld",
    href: "/huenfeld-praforst",
    x: 489,
    y: 810.66,
    width: 121.43,
    height: 32.82,
  },
  {
    title: "Koblenz / Rhein-Mosel",
    href: "/koblenz",
    x: 273.5,
    y: 820.51,
    width: 177.23,
    height: 56,
  },
  {
    title: "Lackenhäuser",
    href: "/lackenhaeuser",
    x: 889.44,
    y: 1087.45,
    width: 188.17,
    height: 33.91,
  },
  {
    title: "Leipzig",
    href: "/leipzig",
    x: 824.89,
    y: 630.15,
    width: 121.44,
    height: 35.01,
  },
  {
    title: "Lübben / Spreewald",
    href: "/luebben-spreewald",
    x: 973.68,
    y: 514.19,
    width: 152.07,
    height: 54.7,
  },
  {
    title: "Meppen",
    href: "/meppen",
    x: 76.58,
    y: 467.15,
    width: 115.97,
    height: 32.82,
  },
  {
    title: "Nassau",
    href: "/nassau",
    x: 319.45,
    y: 948.51,
    width: 108.31,
    height: 37.2,
  },
  {
    title: "Nürnberg",
    href: "/nuernberg",
    x: 677.2,
    y: 998.84,
    width: 140.03,
    height: 38.29,
  },
  {
    title: "Oyten",
    href: "/oyten-bremen",
    x: 381.81,
    y: 463.86,
    width: 120.34,
    height: 53.61,
  },
  {
    title: "Rügen",
    href: "/ruegen",
    x: 795.35,
    y: 175.04,
    width: 106,
    height: 32.82,
  },
  {
    title: "Schloss Langenau",
    href: "/schloss-langenau",
    x: 339.15,
    y: 885.06,
    width: 129.09,
    height: 53.61,
  },
  // {
  //   title: "Saalfeld",
  //   href: "/saalfeld",
  //   x: 834.73,
  //   y: 833.64,
  //   width: 122.52,
  //   height: 33.91,
  // },
  {
    title: "Spieka",
    href: "/spieka-wattenmeer",
    x: 376.34,
    y: 199.11,
    width: 101.74,
    height: 32.82,
  },
  {
    title: "Viechtach",
    href: "/viechtach",
    x: 695.79,
    y: 1076.51,
    width: 137.85,
    height: 35.01,
  },
  {
    title: "Walkenried",
    href: "/walkenried",
    x: 432.14,
    y: 578.74,
    width: 156.44,
    height: 32.82,
  },
  {
    title: "Wingst",
    href: "/wingst",
    x: 512,
    y: 215.52,
    width: 103.93,
    height: 31.73,
  },
];

const CampsiteOverview = ({ blok }: CampsiteOverviewProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [state, dispatch] = useReducer(reducer, { type: "card" });
  const [campsites, setCampsites] = useState<CampsiteStoryblok[]>([]);

  // Initialize state based on URL parameter
  useEffect(() => {
    const viewParam = searchParams.get("view");
    const isDeutschlandkartePath = pathname.endsWith("/deutschlandkarte");
    const view =
      viewParam === "map" || viewParam === "image" || viewParam === "card"
        ? viewParam
        : isDeutschlandkartePath
          ? "image"
          : null;

    if (view) {
      dispatch({ type: view });
    }

    if (view === "image") {
      track("Deutschlandkarte");
    }
  }, [searchParams, pathname]);

  useEffect(() => {
    const fetchData = async () => {
      const res = await fetch("/api/campsites");
      const json = await res.json();
      setCampsites(json);
    };
    fetchData();
  }, []);

  const handleViewChange = (viewType: "card" | "map" | "image") => {
    dispatch({ type: viewType });

    // Track image view specifically
    if (viewType === "image") {
      track("Deutschlandkarte");
    }

    // Update URL without page reload
    const params = new URLSearchParams(searchParams);
    params.set("view", viewType);
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  const hasOffer = (current: CampsiteStoryblok) =>
    campsites.some(
      (campsite) => campsite.uuid == current.uuid && campsite.offers > 0,
    );

  return (
    <article className="w-full" {...storyblokEditable(blok)}>
      <section className="py-8 mx-2 lg:mx-4 text-center">
        <H2 as="h1" colorScheme="primary">
          {blok.title}
        </H2>
        <BodyText className="max-w-md mx-auto mt-2 text-center">
          {blok.description}
        </BodyText>

        <Divider />

        <div className="inline-flex shadow-sm mb-8" role="group">
          <button
            type="button"
            className={clsx(
              "inline-flex gap-2 items-center px-4 py-2 text-sm font-medium bg-white border border-r-0 border-gray-200 hover:bg-gray-100 hover:text-brand-primary100 focus:z-10 focus:ring-2 focus:ring-brand-primary100 focus:text-brand-primary100",
              {
                "text-gray-500": state.type !== "card",
                "text-brand-primary100": state.type === "card",
              },
            )}
            onClick={() => handleViewChange("card")}
            aria-pressed={state.type === "card"}
          >
            <PhotoIcon className="h-5 w-5" aria-hidden="true" />
            Listenansicht
          </button>
          <button
            type="button"
            className={clsx(
              "inline-flex gap-2 items-center px-4 py-2 text-sm font-medium bg-white border border-gray-200 hover:bg-gray-100 hover:text-brand-primary100 focus:z-10 focus:ring-2 focus:ring-brand-primary100 focus:text-brand-primary100",
              {
                "text-gray-500": state.type !== "map",
                "text-brand-primary100": state.type === "map",
              },
            )}
            onClick={() => handleViewChange("map")}
            aria-pressed={state.type === "map"}
          >
            <MapPinIcon className="h-5 w-5" aria-hidden="true" />
            Kartenansicht
          </button>
          <button
            type="button"
            className={clsx(
              "inline-flex gap-2 items-center px-4 py-2 text-sm font-medium bg-white border border-gray-200 hover:bg-gray-100 hover:text-brand-primary100 focus:z-10 focus:ring-2 focus:ring-brand-primary100 focus:text-brand-primary100",
              {
                "text-gray-500": state.type !== "image",
                "text-brand-primary100": state.type === "image",
              },
            )}
            onClick={() => handleViewChange("image")}
            aria-pressed={state.type === "image"}
          >
            <MapPinIcon className="h-5 w-5" aria-hidden="true" />
            Deutschlandkarte
          </button>
        </div>

        {state.type === "map" && (
          <div className="h-[1024px]">
            <Map campsites={blok.campsites as unknown as CampsiteStoryblok[]} />
          </div>
        )}

        {state.type === "card" && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {blok.campsites?.map((nestedBlok: any) => (
              <Card
                key={nestedBlok.id}
                image={nestedBlok.content?.image}
                href={nestedBlok.full_slug}
                name={nestedBlok.name}
                text={nestedBlok.content?.lead}
                isNew={nestedBlok.content.is_new}
                buttonLabel="Entdecken"
                hasOffer={hasOffer(nestedBlok)}
              />
            ))}
          </div>
        )}

        {state.type === "image" && (
          <div className="bg-[#F4F2F5] w-full">
            <svg
              className="w-full h-auto max-w-6xl mx-auto"
              viewBox="0 0 1152 1519"
              xmlns="http://www.w3.org/2000/svg"
              role="img"
              aria-label="Interaktive Karte von Deutschland mit KNAUS Campingparks"
            >
              <title>KNAUS Campingparks Karte Deutschland</title>
              <desc>
                Interaktive Karte zeigt alle KNAUS Campingpark Standorte in
                Deutschland. Klicken Sie auf einen Standort für weitere
                Informationen.
              </desc>

              <style>{`
        .image-mapper-shape {
          fill: rgba(0, 0, 0, 0);
          stroke: transparent;
          stroke-width: 2px;
          transition: all 0.2s ease;
        }
        .image-mapper-link:hover .image-mapper-shape,
        .image-mapper-link:focus .image-mapper-shape {
          stroke: #995672;
          fill: rgba(153, 86, 114, 0.1);
          outline: none;
        }
        .image-mapper-link:focus {
          outline: 2px solid #995672;
          outline-offset: 2px;
        }
      `}</style>

              <image href="/deutschlandkarte.webp" width="1152" height="1519" />

              {locations.map((loc, i) => (
                <a
                  key={i}
                  href={loc.href}
                  className="image-mapper-link"
                  aria-label={`Campingpark ${loc.title} - Weitere Informationen`}
                  role="button"
                  tabIndex={0}
                >
                  <title>{loc.title}</title>
                  <rect
                    x={loc.x}
                    y={loc.y}
                    width={loc.width}
                    height={loc.height}
                    className="image-mapper-shape"
                    role="presentation"
                  />
                </a>
              ))}
            </svg>

            {/* Alternative text-based navigation for screen readers */}
            <nav className="sr-only" aria-label="Liste aller Campingparks">
              <h2>Alle KNAUS Campingparks</h2>
              <ul>
                {locations.map((loc, i) => (
                  <li key={i}>
                    <a href={loc.href}>{loc.title}</a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}
      </section>
    </article>
  );
};

export default CampsiteOverview;
